import {useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {
  useCreateKycRecordApi,
  useUpdateKycRecordApi,
} from "../../../../react-query-hooks/kyc";
import {CreateKycRecordApiResponse} from "../../../../react-query-hooks/kyc/types";
import {useCreateKycContext} from "../context/create-kyc";
import {useGetKycStatusContext} from "../../get-kyc-status/context/get-kyc-status";
import {createKycRecordDtoV2} from "../create-live-event-service";
import {CreateKycBasicFormStepFormValues} from "../multi-step/basic-details/schema-validation";

interface HookResult {
  createKycRecord(values: CreateKycBasicFormStepFormValues): void;
  wasKycRecordCreated: boolean;
  isCreatingKycRecord: boolean;
  responseData?: CreateKycRecordApiResponse;
}

/**
 * Hook for Step 1 (basic details) of the KYC creation flow.
 *
 * - Calls POST /kyc to create KYC record with basic details (no docs)
 * - On success: stores kycId in context, saves form data, advances to Step 2
 * - If existingKycId present: calls PATCH /kyc to update instead
 * - Shows loading spinner during API call
 */
export function useCreateKycRecordHook(existingKycId?: number): HookResult {
  const queryClient = useQueryClient();
  const {dispatch, state: createKycState} = useCreateKycContext();
  const {state: kycStatusState} = useGetKycStatusContext();
  const createKycRecordMutation = useCreateKycRecordApi();
  const updateKycRecordMutation = useUpdateKycRecordApi();

  // Determine effective kycId: status context takes precedence, then create context
  const effectiveKycId = existingKycId ?? createKycState.kycId ?? null;

  const createKycRecord = (values: CreateKycBasicFormStepFormValues) => {
    const handleSuccess = (kycId?: number) => {
      dispatch({type: "update-basic-form-data", payload: values});
      if (kycId) {
        dispatch({type: "update-kyc-id", payload: kycId});
      }
      dispatch({type: "update-create-kyc-form-step", payload: "documentUpload"});
      // Keep user on form for Step 2 (status will be "in_progress" after create)
      kycStatusState.setKycStatusOverride(true);
      setTimeout(() => {
        queryClient.invalidateQueries({queryKey: ["kyc-status"]});
      }, 2);
    };

    // Call API with basic details (no docs) — docs uploaded separately in Step 2
    const handleError = (error: {message?: string}) => {
      const serverMessage = error?.message ?? "Failed to save. Please try again.";
      message.error(serverMessage);
    };

    if (effectiveKycId) {
      // UPDATE existing record (user navigated back, or rejected user resubmitting)
      const dto = {...createKycRecordDtoV2(values), kycId: effectiveKycId};
      updateKycRecordMutation.mutate(dto, {
        onSuccess: (response) => handleSuccess(response.data.id),
        onError: handleError,
      });
    } else {
      // CREATE new record
      const dto = createKycRecordDtoV2(values);
      createKycRecordMutation.mutate(dto, {
        onSuccess: (response) => handleSuccess(response.data.id),
        onError: handleError,
      });
    }
  };

  // Track mutation loading state based on which path is active
  const activeMutation = effectiveKycId
    ? updateKycRecordMutation
    : createKycRecordMutation;

  return {
    createKycRecord,
    wasKycRecordCreated: activeMutation.isSuccess,
    isCreatingKycRecord: activeMutation.isLoading,
    responseData: activeMutation.isSuccess ? activeMutation.data.data : undefined,
  };
}
